import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Empty, StatCard, useIsClient, statusLabel } from "@/components/common";
import { cn } from "@/lib/utils";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import EngagementWorkspace from "./EngagementWorkspace";
import SupportHelp from "./SupportHelp";
import ConfigureCategories from "./ConfigureCategories";
import MasterRequests from "./MasterRequests";
import HelpCenter from "./HelpCenter";
import { toast } from "sonner";
import { Plus, FolderKanban, Loader2, MoreVertical, Archive, Trash2, RotateCcw, ArchiveRestore, History } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { EngagementDeleteDialog, PermanentDeleteDialog } from "./EngagementLifecycle";

export default function DataCollection() {
  return (
    <Routes>
      <Route index element={<Engagements />} />
      <Route path="engagements/:id" element={<EngagementWorkspace />} />
      <Route path="engagements/:id/master" element={<MasterRequests />} />
      <Route path="configure" element={<ConfigureCategories />} />
      <Route path="help" element={<HelpCenter />} />
      <Route path="support" element={<SupportHelp />} />
    </Routes>
  );
}

const VIEWS = [
  { key: "active", label: "Active" },
  { key: "archived", label: "Archived" },
  { key: "deleted", label: "Deleted", adminOnly: true },
];

function Engagements() {
  const nav = useNavigate();
  const { session } = useAuth();
  const [engs, setEngs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("active");
  const [f, setF] = useState({ name: "", client_org_name: "" });
  const [mode, setMode] = useState("scratch"); // scratch | rollforward
  const canManage = session?.roles?.some((r) => ["partner", "manager", "tenant_admin"].includes(r));
  const isTenantAdmin = session?.roles?.includes("tenant_admin");
  const isClient = useIsClient();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permTarget, setPermTarget] = useState(null);

  const load = useCallback((v) => {
    setLoading(true);
    api.get(`/m1/engagements?view=${v}`).then((r) => setEngs(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(view); }, [load, view]);

  const create = async () => {
    if (!f.name || !f.client_org_name) return toast.error("Name and client org required");
    try {
      const r = await api.post("/m1/engagements", f);
      toast.success("Engagement created");
      setOpen(false);
      const newId = r.data.id; const rf = mode === "rollforward";
      setF({ name: "", client_org_name: "" }); setMode("scratch");
      nav(`/m1/engagements/${newId}${rf ? "?rollforward=1" : ""}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const act = async (id, path, label) => {
    try { await api.post(`/m1/engagements/${id}/${path}`); toast.success(label); load(view); }
    catch (e) { toast.error(apiError(e)); }
  };

  const availableViews = VIEWS.filter((v) => !v.adminOnly || isTenantAdmin);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline text-azx-collection">Data Collection</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Engagements</h1>
          <p className="mt-1 text-sm text-slate-500">Structured document & information collection per client.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="create-engagement-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" /> New engagement</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create engagement</DialogTitle><DialogDescription>Set up a new client engagement and choose how its request list starts.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div><Label>Engagement name</Label><Input data-testid="eng-name" className="mt-1.5" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Acme FY2026 Collection" /></div>
                <div><Label>Client organization</Label><Input data-testid="eng-client" className="mt-1.5" value={f.client_org_name} onChange={(e) => setF({ ...f, client_org_name: e.target.value })} /></div>
                <div>
                  <Label className="mb-2 block">Start options</Label>
                  <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
                    <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 dark:border-slate-700 p-3 cursor-pointer">
                      <RadioGroupItem value="scratch" className="mt-0.5" data-testid="eng-mode-scratch" />
                      <div><div className="text-sm font-medium">Start from scratch</div><div className="text-xs text-slate-400">Empty request list.</div></div>
                    </label>
                    <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 dark:border-slate-700 p-3 cursor-pointer">
                      <RadioGroupItem value="rollforward" className="mt-0.5" data-testid="eng-mode-rollforward" />
                      <div><div className="text-sm font-medium">Roll forward from a previous engagement</div><div className="text-xs text-slate-400">Copy last year's request structure after creating.</div></div>
                    </label>
                  </RadioGroup>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="eng-save">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-1.5 mb-5">
        {availableViews.map((v) => (
          <button key={v.key} data-testid={`eng-view-${v.key}`} onClick={() => setView(v.key)}
            className={cn("rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors",
              view === v.key ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 hover:bg-slate-200")}>
            {v.label}
          </button>
        ))}
      </div>

      {!loading && engs.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-3 text-[11px] text-slate-500" data-testid="status-legend">
          {!isClient && <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-orange-600" /> Received</span>}
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-orange-400" /> {isClient ? "Received" : "Awaiting Client"}</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-amber-300" /> {statusLabel("Submitted", isClient)}</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-emerald-500" /> Complete</span>
        </div>
      )}

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        : engs.length === 0 ? <Empty icon={FolderKanban} title={view === "active" ? "No engagements yet" : `No ${view} engagements`} hint={view === "active" ? "Create your first engagement to start collecting documents." : undefined} />
        : (
          <div className="space-y-2" data-testid="engagements-list">
            {engs.map((e) => (
              <EngagementRow key={e.id} e={e} view={view} canManage={canManage} isTenantAdmin={isTenantAdmin}
                onOpen={() => nav(`/m1/engagements/${e.id}`)} act={act}
                onDelete={() => setDeleteTarget(e)} onPermDelete={() => setPermTarget(e)} />
            ))}
          </div>
        )}

      <EngagementDeleteDialog eng={deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)} onDone={() => { setDeleteTarget(null); load(view); }} />
      <PermanentDeleteDialog eng={permTarget} onOpenChange={(o) => !o && setPermTarget(null)} onDone={() => { setPermTarget(null); load(view); }} />
    </div>
  );
}

const STATUS_SEG = [
  { key: "Received", short: "R", cls: "bg-orange-600" },
  { key: "Awaiting Client", short: "AC", cls: "bg-orange-400" },
  { key: "Submitted", short: "S", cls: "bg-amber-300 text-amber-900" },
  { key: "Complete", short: "C", cls: "bg-emerald-500" },
];

function StatusBar({ counts, total }) {
  const isClient = useIsClient();
  if (!total) {
    return <div className="h-6 w-full rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] text-slate-400" data-testid="status-bar-empty">No requests</div>;
  }
  return (
    <div className="flex h-6 w-full overflow-hidden rounded-md" data-testid="status-bar">
      {STATUS_SEG.map((s) => {
        const n = counts?.[s.key] || 0;
        if (!n) return null;
        const label = statusLabel(s.key, isClient);
        const short = isClient && s.key === "Awaiting Client" ? "R" : s.short;
        const pct = (n / total) * 100;
        return (
          <div key={s.key} title={`${label}: ${n}`} style={{ width: `${pct}%` }}
            className={`flex items-center justify-center text-[11px] font-bold text-white ${s.cls}`}>
            {pct >= 8 ? `${short} ${n}` : n}
          </div>
        );
      })}
    </div>
  );
}

function EngagementRow({ e, view, canManage, isTenantAdmin, onOpen, act, onDelete, onPermDelete }) {
  const total = e.request_count || 0;
  return (
    <div data-testid={`engagement-row-${e.id}`} data-testid-card={`engagement-card-${e.id}`}
      className="group flex items-center gap-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-950/40"><FolderKanban className="h-4.5 w-4.5 text-indigo-600" /></div>
      <button onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-4 text-left" data-testid={`engagement-open-${e.id}`}>
        <div className="min-w-0 w-52 sm:w-64 shrink-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-heading font-semibold text-sm">{e.name}</span>
            {view === "archived" && <span className="flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[9px] font-semibold text-slate-500"><Archive className="h-2.5 w-2.5" /> Archived</span>}
            {view === "deleted" && <span className="flex items-center gap-1 rounded-full bg-red-50 dark:bg-red-950/40 px-1.5 py-0.5 text-[9px] font-semibold text-red-500"><Trash2 className="h-2.5 w-2.5" /> Deleted</span>}
          </div>
          <div className="truncate text-xs text-slate-400">{e.client_org_name}</div>
        </div>
        <div className="flex-1 min-w-0">
          <StatusBar counts={e.status_counts} total={total} />
        </div>
        <div className="hidden md:block w-24 shrink-0 text-right text-xs text-slate-500">{e.complete_count}/{total} done</div>
      </button>
      {canManage && (
        <div onClick={(ev) => ev.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`eng-menu-${e.id}`} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700"><MoreVertical className="h-4 w-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {view === "active" && <>
                <DropdownMenuItem data-testid={`eng-archive-${e.id}`} onClick={() => act(e.id, "archive", "Engagement archived")}><Archive className="mr-2 h-4 w-4" /> Archive</DropdownMenuItem>
                <DropdownMenuItem data-testid={`eng-delete-${e.id}`} onClick={onDelete} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
              </>}
              {view === "archived" && <>
                <DropdownMenuItem data-testid={`eng-unarchive-${e.id}`} onClick={() => act(e.id, "unarchive", "Engagement restored")}><ArchiveRestore className="mr-2 h-4 w-4" /> Unarchive</DropdownMenuItem>
                <DropdownMenuItem data-testid={`eng-delete-${e.id}`} onClick={onDelete} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
              </>}
              {view === "deleted" && <>
                <DropdownMenuItem data-testid={`eng-restore-${e.id}`} onClick={() => act(e.id, "restore", "Engagement restored")}><RotateCcw className="mr-2 h-4 w-4" /> Restore</DropdownMenuItem>
                {isTenantAdmin && <DropdownMenuItem data-testid={`eng-perm-delete-${e.id}`} onClick={onPermDelete} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Permanently delete</DropdownMenuItem>}
              </>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

